<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import { useInventoryStore } from '../../pages/useInventoryStore/';
import { InventoryAlertKinds, isInventoryAlertKinds } from '../../domains/entities/Alert';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { InventoryRepository } from '@/coreInfrastructureModule/Repositories/InventoryRepository';
import { IParentBusiness } from '@/InventoryControlModule/domains/entities/Business';
import AlertCell from '@/corePagesModule/components/table/AlertCell.vue';

// i18n
const t = i18n.t;

// Store
const inventoryStore = useInventoryStore();
const { state } = storeToRefs(inventoryStore);
const { toggleOpenAlert, getTableAlertData, setupMoveWarehouse } = inventoryStore;

// Props

withDefaults(
  defineProps<{
    propBusiness: IParentBusiness;
    propRelativeMonth: number;
  }>(),
  {}
);

// Repository
const inventoryRepository = new InventoryRepository();

/**
 * アラートの追加情報を表示用に変換する処理
 * @param num1 - 基準となる数値
 * @param num2 - 比較対象の数値
 */
const convertAdditionalAlertDataForDisplay = (num1?: number, num2?: number) => {
  if (num2) {
    return ((num1 ?? 0) - num2).addComma();
  }
  return (num1 ?? 0).addComma();
};

/**
 * アラートの次へボタンをクリックしたときのイベント
 * @param alertKind - アラートの種類
 * @param business - 取引
 * @param monthDistance - 月の差分
 */
const onMoveWarehouseCheckButton = async (alertKind: string, business: IParentBusiness, monthDistance: number) => {
  if (!isInventoryAlertKinds(alertKind)) return;
  if (alertKind === InventoryAlertKinds.StorageMoved) {
    state.value.isOpeningAlertBusinessId = '';
    state.value.moveWarehouseModalState.isLoadingModalOpen = true;
    const targetYearMonth: IYearMonth = DateService.addMonthsToCurrentDate(monthDistance);
    await setupMoveWarehouse(inventoryRepository, business, targetYearMonth);
  }
};
</script>

<template>
  <AlertCell
    :alert-data="getTableAlertData(propBusiness, propRelativeMonth).map((data) => data.data)"
    :prop-is-opening="state.isOpeningAlertBusinessId === propBusiness.businessId"
    icon-warning="alertCellWarningOnInventoryControl"
    icon-error="alertCellErrorOnInventoryControl"
    @on-next="onMoveWarehouseCheckButton($event, propBusiness, propRelativeMonth)"
    @click="
      (isOpening) => {
        toggleOpenAlert(propBusiness.businessId, isOpening);
      }
    "
  >
    <template
      v-if="getTableAlertData(propBusiness, propRelativeMonth, InventoryAlertKinds.ArrivalGreaterThanOrder).length === 1"
      #[`alertBody-${InventoryAlertKinds.ArrivalGreaterThanOrder}`]
    >
      <template
        v-for="(alertDetum, alertIndex) in getTableAlertData(propBusiness, propRelativeMonth, InventoryAlertKinds.ArrivalGreaterThanOrder)"
        :key="`${propBusiness.businessId}-alert-body-${InventoryAlertKinds.ArrivalGreaterThanOrder}-${alertIndex}`"
      >
        {{ `${t('対象月')}: ${alertDetum.additionalData?.yearMonth.year}/${alertDetum.additionalData?.yearMonth.month}` }}
        <div class="alert-body__content__table">
          <div class="alert-body__content__table--head alert-body__content__table--head--arrival">
            {{ t('入荷数量') }}
          </div>
          <div class="alert-body__content__table--head alert-body__content__table--head--order">
            {{ t('発注数量') }}
          </div>
          <div class="alert-body__content__table--head alert-body__content__table--head--diff">
            {{ t('差分') }}
          </div>
          <div class="alert-body__content__table--data alert-body__content__table--data--arrival">
            {{ convertAdditionalAlertDataForDisplay(alertDetum.additionalData?.arrivalAmount) }}
          </div>
          <div class="alert-body__content__table--data alert-body__content__table--data--order">
            {{ convertAdditionalAlertDataForDisplay(alertDetum.additionalData?.orderAmount) }}
          </div>
          <div class="alert-body__content__table--data alert-body__content__table--data--diff">
            {{ convertAdditionalAlertDataForDisplay(alertDetum.additionalData?.arrivalAmount, alertDetum.additionalData?.orderAmount) }}
          </div>
        </div>
      </template>
    </template>
    <template
      v-if="getTableAlertData(propBusiness, propRelativeMonth, InventoryAlertKinds.OrderGreaterThanArrival).length === 1"
      #[`alertBody-${InventoryAlertKinds.OrderGreaterThanArrival}`]
    >
      <template
        v-for="(alertDetum, alertIndex) in getTableAlertData(propBusiness, propRelativeMonth, InventoryAlertKinds.OrderGreaterThanArrival)"
        :key="`${propBusiness.businessId}-alert-body-${InventoryAlertKinds.OrderGreaterThanArrival}-${alertIndex}`"
      >
        {{ `${t('対象月')}: ${alertDetum.additionalData?.yearMonth.year}/${alertDetum.additionalData?.yearMonth.month}` }}
        <div class="alert-body__content__table">
          <div class="alert-body__content__table--head alert-body__content__table--head--arrival">
            {{ t('入荷数量') }}
          </div>
          <div class="alert-body__content__table--head alert-body__content__table--head--order">
            {{ t('発注数量') }}
          </div>
          <div class="alert-body__content__table--head alert-body__content__table--head--diff">
            {{ t('差分') }}
          </div>
          <div class="alert-body__content__table--data alert-body__content__table--data--arrival">
            {{ convertAdditionalAlertDataForDisplay(alertDetum.additionalData?.arrivalAmount) }}
          </div>
          <div class="alert-body__content__table--data alert-body__content__table--data--order">
            {{ convertAdditionalAlertDataForDisplay(alertDetum.additionalData?.orderAmount) }}
          </div>
          <div class="alert-body__content__table--data alert-body__content__table--data--diff">
            {{ convertAdditionalAlertDataForDisplay(alertDetum.additionalData?.arrivalAmount, alertDetum.additionalData?.orderAmount) }}
          </div>
        </div>
      </template>
    </template>
  </AlertCell>
</template>
